import { useCallback, useEffect, useRef } from 'react';
import type { StepChannel, Unsubscribe } from '../types';
import { StepChannelImpl } from '../StepChannel';

interface UseWizardChannelsOptions {
  wizardId: string;
  onSignalReceived?: () => void;
}

interface UseWizardChannelsResult {
  activateStep(stepId: string): StepChannel;
  getChannel(stepId: string): StepChannel | undefined;
  disposeAll(): void;
}

/**
 * Manages the lifecycle of StepChannels for a single wizard instance.
 * Creates channels lazily when steps activate, disposes them on teardown.
 */
export function useWizardChannels({
  wizardId,
  onSignalReceived,
}: UseWizardChannelsOptions): UseWizardChannelsResult {
  const channelsRef = useRef<Map<string, StepChannel>>(new Map());
  const unsubsRef = useRef<Map<string, Unsubscribe>>(new Map());

  const disposeAll = useCallback(() => {
    for (const unsub of unsubsRef.current.values()) unsub();
    unsubsRef.current.clear();

    for (const channel of channelsRef.current.values()) channel.dispose();
    channelsRef.current.clear();
  }, []);

  const activateStep = useCallback(
    (stepId: string): StepChannel => {
      const existing = channelsRef.current.get(stepId);
      if (existing && existing.status !== 'disposed') {
        return existing;
      }

      if (existing?.status === 'disposed') {
        const unsub = unsubsRef.current.get(stepId);
        unsub?.();
        unsubsRef.current.delete(stepId);
        channelsRef.current.delete(stepId);
      }

      const channel = new StepChannelImpl(wizardId, stepId);

      if (onSignalReceived) {
        const unsub = channel.onSignal(() => onSignalReceived());
        unsubsRef.current.set(stepId, unsub);
      }

      channelsRef.current.set(stepId, channel);
      return channel;
    },
    [wizardId, onSignalReceived],
  );

  const getChannel = useCallback((stepId: string): StepChannel | undefined => {
    return channelsRef.current.get(stepId);
  }, []);

  useEffect(() => disposeAll, [disposeAll]);

  return { activateStep, getChannel, disposeAll };
}
